import { Location, LocationType, StudioZone } from '@talon/web/lib/api';
import { BoxProps } from 'grommet';
import { MapLocation } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Location as LocationComponent } from '.';
import { Panel } from '..';
import { useProjectStore } from '../../../context';

export type LocationPanelProps = {
  cardProps?: BoxProps;
  headerBackground?: string;
};

export const LocationPanel = observer(({ cardProps, headerBackground }: LocationPanelProps) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const projectStore = useProjectStore();

  useEffect(() => {
    const projectId = projectStore.currentProject?.id;
    if (!projectId) return;
    const getLocations = async () => {
      const locs = await projectStore.getLocationsByProjectId(projectId);
      setLocations(locs || []);
    };
    getLocations();
  }, [projectStore.currentProject?.id]);

  const newLocation = async () => {
    if (!projectStore.currentProject?.id) return;
    const location = {
      name: 'Location Name',
      type: LocationType.Nearby,
      street_address: '',
      aptOrUnit: '',
      city: '',
      state: '',
      zip: '',
      projectId: projectStore.currentProject.id,
      id: 'new',
      created_at: '',
      updated_at: '',
      project: projectStore.currentProject,
      session_activities: [],
    };

    setLocations([...locations, location]);
  };

  const deleteLocations = async (ids: string[]) => {
    for (const id of ids) {
      await projectStore.deleteLocation(id);
    }
    setLocations(locations.filter((l) => !ids.includes(l.id)));
  };

  const updateLocation = async (update: Location, index: number) => {
    if (!projectStore.currentProject?.id || !update) return;
    if (update.id === 'new') {
      if (
        !update.name ||
        !update.street_address ||
        !update.city ||
        !update.state ||
        !update.zip ||
        !update.type ||
        !update.lat ||
        !update.lng
      )
        return;

      const updateRequest = {
        ...update,
        id: undefined, // Ensure new location does not have an ID
        created_at: undefined,
        updated_at: undefined,
      };
      const location = await projectStore.createLocation({
        ...updateRequest,
        projectId: projectStore.currentProject?.id,
      });
      if (!location) return;
      // replace the 'new' location with the created one
      setLocations(locations.map((loc) => (loc.id === 'new' ? location : loc)));
      return;
    }
    const location = await projectStore.updateLocation(update);
    if (!location) return;
    const newLocations = [...locations];
    newLocations[index] = location;
    setLocations(newLocations);
  };

  return (
    <Panel
      items={locations}
      newItem={newLocation}
      deleteItems={deleteLocations}
      updateItem={updateLocation}
      label="Location"
      labelPlural="Locations"
      addIcon={<MapLocation />}
      types={[
        {
          label: 'Location Type',
          key: 'type',
          values: Object.values(LocationType),
        },
        {
          label: 'Studio Zone',
          key: 'studio_zone',
          values: Object.values(StudioZone),
          readonly: true,
        },
      ]}
      cardProps={cardProps}
      color="accent-1"
      headerBackground={headerBackground}
    >
      {locations.map(({ session_activities, project, ...location }, i) => (
        <LocationComponent
          place={location}
          id={location.id}
          updatePlace={(p) => updateLocation({ ...locations[i], session_activities, project, ...p }, i)}
          key={location.id}
        />
      ))}
    </Panel>
  );
});
